import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormLabel,
  TextField,
} from '@mui/material';
import { KitButton } from 'components/common/cms-kit';

import FileUpload from './FileUpload';
import { CategoryTagList, SecureFileRecord } from 'types/SecureFileTypes';
import {
  allowedExtensions,
  canUploadFile,
  maxSecureFileSize,
} from 'services/secureFileUtils';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import PleaseWait from 'components/common/PleaseWait';

type SecureFileFormProps = {
  categoryTagList: CategoryTagList;
  categoryTagDef: { [category: string]: boolean };
  onSubmit: (data: {
    friendlyName: string;
    tags: { [category: string]: string[] };
    file: File | null;
    successCallback: () => void;
  }) => void;
  isSubmitting?: boolean;
  isOpen: boolean;
  onClose: () => void;
  editFile: SecureFileRecord | null;
  mode: 'add' | 'edit';
};

const SecureFileForm: React.FC<SecureFileFormProps> = ({
  categoryTagList,
  categoryTagDef,
  onSubmit,
  isSubmitting = false,
  isOpen,
  onClose,
  editFile,
  mode,
}) => {
  const [friendlyName, setFriendlyName] = useState('');
  const [friendlyNameError, setFriendlyNameError] = useState(false);
  const [selectedTags, setSelectedTags] = useState<{
    [category: string]: string[];
  }>({});
  const [tagError, setTagError] = useState<{ [category: string]: boolean }>({});
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (editFile) {
      setFriendlyName(editFile.friendly_file_name);
      setSelectedTags(editFile.tags);
    }
  }, [editFile]);

  const handleTagChange = (category: string, tag: string) => {
    setSelectedTags((prev) => {
      const tags = prev[category] || [];
      if (tags.includes(tag)) {
        return { ...prev, [category]: tags.filter((t) => t !== tag) };
      } else {
        return { ...prev, [category]: [...tags, tag] };
      }
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadFile = e.target.files?.[0] || null;
    if (!uploadFile) return;

    if (!canUploadFile(uploadFile.name, uploadFile.type)) {
      enqueueSnackbar(
        `Only files of type ${allowedExtensions.join(', ')} are allowed.`,
        {
          variant: 'error',
          action: (key) => (
            <button onClick={() => closeSnackbar(key)}>Dismiss</button>
          ),
        },
      );
      return;
    }
    if (uploadFile.size > maxSecureFileSize) {
      enqueueSnackbar(
        `File size exceeds the maximum limit of ${maxSecureFileSize / 1024 / 1024} MB.`,
        {
          variant: 'error',
          action: (key) => (
            <button onClick={() => closeSnackbar(key)}>Dismiss</button>
          ),
        },
      );
      return;
    }
    setFile(uploadFile);
  };

  const canSubmit = () => {
    let valid = true;
    if (friendlyName.trim().length === 0) {
      setFriendlyNameError(true);
      valid = false;
    }
    const requiredCategories = Object.keys(categoryTagDef).filter(
      (category) => categoryTagDef[category],
    );
    Object.entries(categoryTagList).forEach(([category, _tags]) => {
      if (!selectedTags[category] || selectedTags[category].length === 0) {
        if (requiredCategories.includes(category)) {
          setTagError((prev) => ({ ...prev, [category]: true }));
          valid = false;
        }
      }
    });
    if (mode === 'add' && !file) {
      valid = false;
    }
    if (!valid) {
      return false;
    }
    setFriendlyNameError(false);
    setTagError({});
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit()) return;
    onSubmit({
      friendlyName,
      tags: selectedTags,
      file,
      successCallback: () => {
        resetForm();
      },
    });
  };

  const resetForm = () => {
    setFriendlyName('');
    setSelectedTags({});
    setFile(null);
  };

  return (
    <Dialog
      maxWidth="sm"
      scroll="paper"
      fullWidth={true}
      open={isOpen}
      onClose={onClose}
    >
      <DialogTitle>Add Secure File</DialogTitle>
      <DialogContent>
        <PleaseWait isLoading={isSubmitting} />
        <form onSubmit={handleSubmit} className="space-y-6">
          <TextField
            size="small"
            margin="dense"
            value={friendlyName}
            error={friendlyNameError}
            required
            id="friendlyName"
            label="Friendly File Name"
            type="text"
            fullWidth
            variant="outlined"
            onChange={(e) => setFriendlyName(e.target.value)}
            slotProps={{
              htmlInput: { maxLength: 256 },
              inputLabel: { shrink: true }
            }} />

          {Object.entries(categoryTagList).map(([category, tags]) => (
            <FormGroup key={category}>
              <FormLabel
                component="legend"
                required={categoryTagDef[category]}
                error={tagError[category]}
              >{`${category} Tags`}</FormLabel>
              <div className="flex flex-wrap gap-2 mt-1">
                {tags.map((tag) => (
                  <FormControlLabel
                    key={tag}
                    label={tag}
                    control={
                      <Checkbox
                        value={tag}
                        checked={selectedTags[category]?.includes(tag) || false}
                        onChange={() => handleTagChange(category, tag)}
                      />
                    }
                  />
                ))}
              </div>
            </FormGroup>
          ))}

          <div>
            <FileUpload
              handleAddFile={handleFileChange}
              disabled={mode === 'edit'}
            />
            {file && (
              <div className="mt-2 text-sm text-gray-600">
                Selected file: <span className="font-medium">{file.name}</span>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <KitButton
              onClick={() => {
                resetForm();
                onClose();
              }}
              round
              size="sm"
            >
              Cancel
            </KitButton>
            <KitButton
              onClick={handleSubmit}
              round
              size="sm"
              color="success"
              disabled={(mode === 'add' && !file) || isSubmitting}
            >
              {mode === 'add' ? 'Add' : 'Update'} File
            </KitButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SecureFileForm;
